import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { memo, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NavigationVersionContext } from "../app/navigation/navigation-version-context";
import { OrderManagementSuiteStatusesContext } from "./order-management-cloud";
import { SupportSuiteStatusesContext } from "./support-cloud";

export const MerchantRouteNavigate = memo(function MerchantRoute({
  routeTo,
}: {
  routeTo: (routes: MerchantRoutes) => string;
}) {
  const navigate = useNavigate();
  const routes = useContext(MerchantRoutesContext);

  useEffect(() => {
    if (!routes) {
      return;
    }
    const href = routeTo(routes);
    navigate(href, { replace: true });
  }, [navigate, routes, routeTo]);

  return null;
});

export const MerchantRoutesProvider = memo(function MerchantRoutesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useContext(AuthContext);
  const team = useContext(TeamContext);

  const { omsEnabled, warrantiesEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const { supportV2Enabled } = useContext(SupportSuiteStatusesContext) ?? {};
  const permissions = useContext(PermissionsContext);
  const { newNavigationEnabled } = useContext(NavigationVersionContext);

  const teamId = auth?.teamId;

  const baseUrl = teamId ? `/stores/${teamId}` : "";
  const storeBaseUrl = baseUrl;
  const homeBaseHref = `${storeBaseUrl}/home`;
  // When the new-navigation flag is OFF, `shippingBaseUrl` and
  // `returnsBaseUrl` collapse to `${baseUrl}/order-management`. That single
  // root is where all legacy Operations pages live, so every derived href
  // below automatically resolves to the correct legacy URL without any
  // additional branching. When the flag is ON, they split into the new
  // `/shipping` and `/returns` trees.
  const orderManagementBaseUrl = `${baseUrl}/order-management`;
  const shippingBaseUrl = newNavigationEnabled
    ? `${baseUrl}/shipping`
    : orderManagementBaseUrl;
  const returnsBaseUrl = newNavigationEnabled
    ? `${baseUrl}/returns`
    : orderManagementBaseUrl;
  const inventoryManagementBaseUrl = `${baseUrl}/inventory`;
  const marketingBaseUrl = `${baseUrl}/marketing`;
  const supportBaseUrl = supportV2Enabled
    ? `${baseUrl}/support-v2`
    : `${baseUrl}/support`;
  const conversionBaseUrl = `${baseUrl}/conversion`;
  const customersBaseUrl = `${baseUrl}/customer`;
  const financeBaseUrl = `${baseUrl}/finance`;
  const redoSuppliesBaseUrl = `${baseUrl}/redo-supplies`;
  const messagingBaseUrl = `${baseUrl}/messaging`;
  const aeoBaseUrl = `${baseUrl}/aeo`;

  const claimsHref = `${returnsBaseUrl}/claims`;

  const shippingDefaultRoute = useMemo(() => {
    const canManageShipments = permissions?.has(
      OrderManagementPermissions.MANAGE_SHIPMENTS,
    );
    const canScanAndVerify = permissions?.has(
      OrderManagementPermissions.SCAN_AND_VERIFY,
    );
    const canViewDashboard = permissions?.has(
      OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
    );

    const dashboardRoute = `${shippingBaseUrl}/outbound-labels/dashboard`;
    const omsDefault = `${shippingBaseUrl}/outbound-labels/awaiting-shipment`;

    if (team?.settings?.outboundLabels?.newOmsEnabled && canViewDashboard) {
      return dashboardRoute;
    }
    if (omsEnabled && canManageShipments) {
      return omsDefault;
    } else if (omsEnabled && canScanAndVerify) {
      return `${shippingBaseUrl}/outbound-labels/scan`;
    }
    return dashboardRoute;
  }, [permissions, omsEnabled, shippingBaseUrl, team]);

  const returnsDefaultRoute = useMemo(() => {
    const canViewReturns = permissions?.has(
      OrderManagementPermissions.VIEW_RETURN,
    );
    const canManageWarranties = permissions?.has(
      OrderManagementPermissions.MANAGE_WARRANTY,
    );

    if (canViewReturns) {
      return `${returnsBaseUrl}/returns`;
    } else if (canManageWarranties && warrantiesEnabled) {
      return `${returnsBaseUrl}/warranties`;
    }
    return `${returnsBaseUrl}/returns`;
  }, [permissions, warrantiesEnabled, returnsBaseUrl]);

  const claimsDefaultRoute = useMemo(() => {
    const canManagePackageProtectionClaims = permissions?.has(
      OrderManagementPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    );

    const canManageCarrierClaims = permissions?.has(
      OrderManagementPermissions.MANAGE_CARRIER_CLAIM,
    );

    const packageProtectionEnabled =
      !!team?.settings.packageProtection?.enabled;

    const omsEnabled = !!team?.settings.outboundLabels?.newOmsEnabled;
    const isReturnEnabled = team?.settings.returns?.enabled;

    if (canManagePackageProtectionClaims && packageProtectionEnabled) {
      return `${claimsHref}/all`;
    } else if (canManageCarrierClaims && (omsEnabled || isReturnEnabled)) {
      return `${claimsHref}/carrier-claims`;
    } else {
      return `${claimsHref}/all`;
    }
  }, [team?.settings, claimsHref, permissions]);

  const merchantRoutes: MerchantRoutes = useMemo(
    () => ({
      // Home routes
      overviewHref: `${homeBaseHref}/overview`,
      analyticsHref: `${homeBaseHref}/analytics`,
      getAnalyticsSubRoute: (subPath: string) =>
        `${homeBaseHref}/analytics/${subPath}`,
      homeBaseHref,

      // Shipping routes
      shippingDefaultRoute,
      pickSessionHref: `${shippingBaseUrl}/outbound-labels/pick-sessions`,
      pickSessionTableHref: `${shippingBaseUrl}/outbound-labels/admin-pick-sessions`,
      transferHref: `${shippingBaseUrl}/warehouse/transfer`,
      instantTransferHref: `${shippingBaseUrl}/warehouse/instant-transfer`,
      receiveToBinHref: `${shippingBaseUrl}/warehouse/receive-to-bin`,
      consolidationHref: `${shippingBaseUrl}/warehouse/consolidation`,

      ordersOutboundLabelsHref: `${shippingBaseUrl}/outbound-labels`,
      getOrdersOutboundLabelsSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/outbound-labels/${subPath}`,

      warehouseHref: `${shippingBaseUrl}/warehouse`,
      warehouseBaseUrl: `${shippingBaseUrl}/warehouse`,

      tariffCalculatorHref: `${shippingBaseUrl}/outbound-labels/tariff-calculator`,
      orderEditsHref: `${shippingBaseUrl}/order-edits`,
      classifyProductsHref: `${shippingBaseUrl}/classify-products`,
      analyticsOrderFulfillmentHref: `${shippingBaseUrl}/analytics/order-fulfillment`,
      getAnalyticsOrderFulfillmentSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/analytics/order-fulfillment?tab=${subPath}`,

      // Returns routes
      returnsDefaultRoute,
      returnsHref: `${returnsBaseUrl}/returns`,
      getReturnsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/returns/${subPath}`,

      claimsHref,
      claimsDefaultRoute,
      getClaimsSubRoute: (subPath: string) => `${claimsHref}/${subPath}`,

      getWarrantiesHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${returnsBaseUrl}/warranties/all-warranties`
          : `${returnsBaseUrl}/warranties/about`,
      warrantiesHref: `${returnsBaseUrl}/warranties`,
      getWarrantiesSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/warranties/${subPath}`,
      registrationsHref: `${returnsBaseUrl}/registrations`,

      returnsProcessingHref: `${returnsBaseUrl}/warehouse/processing`,

      getRecycleHref: () => `${returnsBaseUrl}/recycle/all-recycle`,
      recycleHref: `${returnsBaseUrl}/recycle`,
      getRecycleSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/recycle/${subPath}`,

      analyticsReturnsClaimsHref: `${returnsBaseUrl}/analytics/returns-claims`,
      getAnalyticsReturnsClaimsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/analytics/returns-claims/${subPath}`,
      analyticsReportsHref: `${returnsBaseUrl}/analytics/reports`,
      analyticsReturnsProcessingHref: `${returnsBaseUrl}/analytics/returns-processing`,
      analyticsManagedReturnsHref: `${returnsBaseUrl}/analytics/managed-returns`,

      // Inventory routes
      getInventoryHref: ({ enabled }: { enabled: boolean }) =>
        `${inventoryManagementBaseUrl}`,

      inventoryProductsHref: `${inventoryManagementBaseUrl}/catalog/products`,

      // Marketing routes
      marketingAnalyticsHref: `${marketingBaseUrl}/analytics`,
      automationsHref: `${marketingBaseUrl}/automations`,

      getTrackingHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${marketingBaseUrl}/tracking`
          : `${marketingBaseUrl}/tracking/about`,

      getTrackingSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/tracking/${subPath}`,

      recoverHref: `${marketingBaseUrl}/recover`,
      getRecoverSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/recover/${subPath}`,

      contextualHref: `${marketingBaseUrl}/contextual`,
      getContextualSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/contextual/${subPath}`,

      reviewsHref: `${marketingBaseUrl}/reviews/dashboard`,
      getReviewsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/reviews/${subPath}`,

      emailSmsHref: `${marketingBaseUrl}/email-sms`,
      getEmailSmsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/email-sms/${subPath}`,

      loyaltyHref: `${marketingBaseUrl}/loyalty/program`,
      getLoyaltySubRoute: (subPath: string) =>
        `${marketingBaseUrl}/loyalty/${subPath}`,

      marketingAgentHref: `${marketingBaseUrl}/marketing-agent`,
      getMarketingAgentSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/marketing-agent/${subPath}`,

      textConciergeHref: `${marketingBaseUrl}/text-concierge`,
      getTextConciergeSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/text-concierge/${subPath}`,

      // Messaging routes
      messagingBaseUrl,
      messagingHref: `${messagingBaseUrl}/pre-purchase`,
      getMessagingSubRoute: (subPath: string) =>
        `${messagingBaseUrl}/${subPath}`,

      // Support routes
      getSupportHref: ({ enabled }: { enabled: boolean }) => {
        if (supportV2Enabled) {
          return `${baseUrl}/support-v2`;
        }
        return enabled
          ? `${baseUrl}/support/table/all`
          : `${baseUrl}/support/about`;
      },

      getSupportTableHref: (viewName: string) => {
        if (supportV2Enabled) {
          return `${baseUrl}/support-v2`;
        }
        return `${baseUrl}/support/table/${encodeURIComponent(viewName)}`;
      },
      getSupportTableSubRoute: (subPath: string) => {
        if (supportV2Enabled) {
          return `${baseUrl}/support-v2/${subPath}`;
        }
        return `${baseUrl}/support/table/${subPath}`;
      },
      getConciergeHref: ({ enabled }: { enabled: boolean }) => {
        return enabled
          ? `${baseUrl}/support/dashboard`
          : `${baseUrl}/support/concierge/preview`;
      },
      getConciergeSubRoute: (subPath: string) => {
        return `${baseUrl}/support/concierge/${subPath}`;
      },

      getSupportDevSubRoute: (subPath: string) => {
        return `${baseUrl}/support/dev/${subPath}`;
      },

      // Conversion routes
      spotlightsHref: `${conversionBaseUrl}/spotlights`,
      getSpotlightsSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/spotlights/${subPath}`,
      conversionAnalyticsHref: `${conversionBaseUrl}/analytics`,
      getConversionHref: ({
        enabled,
        checkoutOutboundRatesEnabled,
      }: {
        enabled: boolean;
        checkoutOutboundRatesEnabled: boolean;
      }) => {
        if (enabled && checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/dashboard`;
        } else if (enabled && !checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/onboard`;
        } else {
          return `${conversionBaseUrl}/welcome`;
        }
      },

      // Marketing routes
      getMarketingHref: ({
        emailSmsEnabled,
        marketingCloudEnabled,
      }: {
        emailSmsEnabled: boolean;
        marketingCloudEnabled: boolean;
      }) => {
        if (emailSmsEnabled) {
          return `${marketingBaseUrl}/analytics`;
        } else if (marketingCloudEnabled) {
          return `${marketingBaseUrl}/automations`;
        } else {
          return `${marketingBaseUrl}/welcome`;
        }
      },

      getSegmentBuilderRoute({
        teamId,
        segmentId,
      }: {
        teamId: string;
        segmentId: string;
      }) {
        return `${storeBaseUrl}/marketing/segments/${segmentId}`;
      },

      getCustomerRecordHref: ({ customerId }: { customerId?: string }) =>
        customerId
          ? `${customersBaseUrl}/${customerId}`
          : `${customersBaseUrl}/all-customers`,

      getConversionSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/${subPath}`,

      // Settings routes
      settingsHref: `${storeBaseUrl}/settings`,
      getSettingsSubRoute: (subPath: string) =>
        `${storeBaseUrl}/settings/${subPath}`,

      // Customer portal builder route
      customerPortalBuilderHref: `${storeBaseUrl}/customer-portal-builder`,

      // Base URL helpers
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      storeBaseUrl,
      customersBaseUrl,
      aeoBaseUrl,

      // Finance routes
      financeBaseUrl,
      disputesHref: `${financeBaseUrl}/disputes`,
      disputesDashboardHref: `${financeBaseUrl}/disputes/dashboard`,
      disputesChargebacksHref: `${financeBaseUrl}/disputes/chargebacks`,
      disputesSettingsHref: `${financeBaseUrl}/disputes/settings`,
      disputesIntegrationsHref: `${financeBaseUrl}/disputes/integrations`,

      // Redo Supplies routes
      redoSuppliesBaseUrl,
      redoSuppliesShopHref: `${redoSuppliesBaseUrl}/shop`,
      redoSuppliesOrderHistoryHref: `${redoSuppliesBaseUrl}/order-history`,
      redoSuppliesRecurringPurchasesInventoryHref: `${redoSuppliesBaseUrl}/recurring-purchases/inventory`,
      redoSuppliesRecurringPurchasesTimeHref: `${redoSuppliesBaseUrl}/recurring-purchases/time`,

      // AEO routes
      aeoDashboardHref: `${aeoBaseUrl}/dashboard`,
      aeoIntegrationsHref: `${aeoBaseUrl}/integrations`,
      aeoPromptsHref: `${aeoBaseUrl}/prompts`,
      aeoCatalogHref: `${aeoBaseUrl}/catalog`,
      aeoAssistantHref: `${aeoBaseUrl}/assistant`,
      aeoCompetitorsHref: `${aeoBaseUrl}/competitors`,
      aeoBrandHref: `${aeoBaseUrl}/brand`,
      getAeoSubRoute: (subPath: string) => `${aeoBaseUrl}/${subPath}`,
      getAeoCatalogSubRoute: (subPath: string) =>
        `${aeoBaseUrl}/catalog/${subPath}`,
    }),
    [
      homeBaseHref,
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      messagingBaseUrl,
      aeoBaseUrl,
      financeBaseUrl,
      redoSuppliesBaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      customersBaseUrl,
      storeBaseUrl,
      baseUrl,
      supportV2Enabled,
      shippingDefaultRoute,
      returnsDefaultRoute,
      claimsHref,
      claimsDefaultRoute,
    ],
  );

  return (
    <MerchantRoutesContext.Provider value={merchantRoutes}>
      {children}
    </MerchantRoutesContext.Provider>
  );
});

// Convenience hook
export function useMerchantRoutes(): MerchantRoutes {
  return useRequiredContext(MerchantRoutesContext);
}
